import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Store, Factory, Loader2, ArrowLeft, ArrowRight } from "lucide-react";

const CATS = ["Grocery", "FMCG", "Personal Care", "Home Care", "Stationery", "Cosmetics", "Household", "Beverages", "Other"];

export default function Auth() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { login } = useAuth();
  const [role, setRole] = useState(sp.get("role") === "manufacturer" ? "manufacturer" : "retailer");
  const [mode, setMode] = useState(sp.get("mode") === "login" ? "login" : "register");
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => setPlans([])); }, []);
  useEffect(() => { setErr(""); setOtpSent(false); setOtp(""); setDevOtp(null); }, [role, mode]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function sendOtp() {
    if (!/^\d{10}$/.test(form.mobile || "")) { setErr("Enter a valid 10-digit mobile number first"); return; }
    setSendingOtp(true); setErr("");
    try {
      const { data } = await api.post("/auth/otp/send", { mobile: form.mobile, role });
      setOtpSent(true);
      if (data.dev_mode) { setDevOtp(data.dev_otp); setOtp(data.dev_otp); }
      toast.success("OTP sent to your mobile");
    } catch (e) { setErr(apiError(e)); } finally { setSendingOtp(false); }
  }

  async function submit() {
    setLoading(true); setErr("");
    try {
      let url, payload;
      if (mode === "login") {
        url = `/auth/login/${role}`;
        payload = { email: form.email, password: form.password };
      } else if (role === "manufacturer") {
        url = "/auth/register/manufacturer";
        payload = { company_name: form.company_name, brand_name: form.brand_name, owner_name: form.owner_name,
          mobile: form.mobile, otp, email: form.email, business_category: form.business_category, gstin: form.gstin,
          product_categories: (form.product_categories || "").split(",").map((s) => s.trim()).filter(Boolean),
          address: form.address, website: form.website, password: form.password };
      } else {
        url = "/auth/register/retailer";
        payload = { shop_name: form.shop_name, owner_name: form.owner_name, mobile: form.mobile, otp,
          email: form.email, location: form.location, business_category: form.business_category,
          store_type: form.store_type, gstin: form.gstin, password: form.password };
      }
      const { data } = await api.post(url, payload);
      login(data.token, data.user);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      nav(role === "retailer" ? "/retailer" : "/manufacturer");
    } catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-sgrey p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-7 shadow-sm">
        <button data-testid="auth-back" onClick={() => nav("/")} className="mb-5 flex items-center gap-1 text-sm text-ink/50 hover:text-forest">
          <ArrowLeft className="h-4 w-4" /> Home
        </button>
        <Logo className="mb-6" />

        <Tabs value={role} onValueChange={setRole}>
          <TabsList className="grid w-full grid-cols-2 bg-sgrey">
            <TabsTrigger data-testid="tab-retailer" value="retailer" className="data-[state=active]:bg-forest data-[state=active]:text-white"><Store className="mr-2 h-4 w-4" /> Retailer</TabsTrigger>
            <TabsTrigger data-testid="tab-manufacturer" value="manufacturer" className="data-[state=active]:bg-forest data-[state=active]:text-white"><Factory className="mr-2 h-4 w-4" /> Manufacturer</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="mt-4 flex rounded-md border border-border p-1 text-sm">
          {["register", "login"].map((m) => (
            <button key={m} data-testid={`mode-${m}`} onClick={() => setMode(m)}
              className={`flex-1 rounded px-3 py-1.5 font-medium capitalize transition-colors ${mode === m ? "bg-forest text-white" : "text-ink/60"}`}>
              {m === "register" ? "Create account" : "Sign in"}
            </button>
          ))}
        </div>

        {err && <Alert className="mt-4 border-red-300 bg-red-50"><AlertDescription className="text-red-700" data-testid="auth-error">{err}</AlertDescription></Alert>}

        {mode === "register" && plans.filter((p) => p.segment === role).length > 0 && (
          <div data-testid="auth-plan-preview" className="mt-4 rounded-lg border border-border bg-psage/20 p-3">
            <p className="overline mb-1.5 text-olive">{role === "manufacturer" ? "Manufacturer plans" : "Retailer plans"}</p>
            <div className="space-y-1">
              {plans.filter((p) => p.segment === role).sort((a, b) => a.order - b.order).map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm">
                  <span className="text-ink/70">{p.name}{p.recommended ? " ⭐" : ""}</span>
                  <span className="num font-semibold text-forest">{p.price === null ? "Custom" : p.price === 0 ? "Free" : `${money(p.price).replace(".00", "")}/mo`}</span>
                </div>
              ))}
            </div>
            <button type="button" data-testid="auth-see-pricing" onClick={() => nav("/pricing")} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-forest hover:underline">
              See full pricing <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        )}

        <div className="mt-5 max-h-[52vh] space-y-3 overflow-y-auto pr-1">
          {mode === "login" ? (
            <>
              <Field label="Email" testid="lg-email" type="email" v={form.email} on={(v) => set("email", v)} />
              <Field label="Password" testid="lg-password" type="password" v={form.password} on={(v) => set("password", v)} />
            </>
          ) : role === "manufacturer" ? (
            <>
              <Field label="Company name" testid="rg-company" on={(v) => set("company_name", v)} />
              <Field label="Brand name" testid="rg-brand" on={(v) => set("brand_name", v)} />
              <Field label="Owner name" testid="rg-owner" on={(v) => set("owner_name", v)} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Mobile" testid="rg-mobile" on={(v) => set("mobile", v)} />
                <Field label="Email" testid="rg-email" type="email" on={(v) => set("email", v)} />
              </div>
              <SelectField label="Business category" testid="rg-bizcat" on={(v) => set("business_category", v)} />
              <Field label="GST number" testid="rg-gst" on={(v) => set("gstin", v)} />
              <Field label="Product categories (comma separated)" testid="rg-prodcats" on={(v) => set("product_categories", v)} />
              <Field label="Company address" testid="rg-address" on={(v) => set("address", v)} />
              <Field label="Company website" testid="rg-website" on={(v) => set("website", v)} />
              <Field label="Password" testid="rg-password" type="password" on={(v) => set("password", v)} />
              <p className="text-xs text-ink/50">Your account starts as <b>Verification Pending</b> and needs admin approval before you can list products.</p>
            </>
          ) : (
            <>
              <Field label="Shop name" testid="rg-shop" on={(v) => set("shop_name", v)} />
              <Field label="Owner name" testid="rg-owner" on={(v) => set("owner_name", v)} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Mobile" testid="rg-mobile" on={(v) => set("mobile", v)} />
                <Field label="Email" testid="rg-email" type="email" on={(v) => set("email", v)} />
              </div>
              <Field label="Location (city, state)" testid="rg-location" on={(v) => set("location", v)} />
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="Business category" testid="rg-bizcat" on={(v) => set("business_category", v)} />
                <Field label="Store type" testid="rg-storetype" on={(v) => set("store_type", v)} />
              </div>
              <Field label="GST number (optional)" testid="rg-gst" on={(v) => set("gstin", v)} />
              <Field label="Password" testid="rg-password" type="password" on={(v) => set("password", v)} />
            </>
          )}
        </div>

        <Button data-testid="auth-submit" className="mt-4 w-full bg-forest text-white hover:bg-forest/90" onClick={submit} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Sign in" : "Create account"}
        </Button>
        {mode === "register" && (
          <p className="mt-3 text-center text-xs text-ink/50">New accounts are reviewed by VIPER OWL admin before you can {role === "manufacturer" ? "list & sell products" : "place orders"}.</p>
        )}
      </div>
    </div>
  );
}

function Field({ label, on, testid, type = "text", v }) {
  return (<div><Label className="text-xs">{label}</Label>
    <Input data-testid={testid} type={type} value={v ?? undefined} onChange={(e) => on(e.target.value)} className="mt-1" /></div>);
}
function SelectField({ label, on, testid }) {
  return (<div><Label className="text-xs">{label}</Label>
    <Select onValueChange={on}><SelectTrigger data-testid={testid} className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>);
}
